import { useState } from 'react';
import { Masthead } from '@/components/Masthead';
import { NotificationItem } from '@/components/NotificationItem';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonLine } from '@/components/Skeleton';
import { Button } from '@/components/ui/Button';
import {
  useMarkAllRead,
  useNotifications,
  type NotificationSeverity,
} from '@/lib/notifications';
import { cn } from '@/lib/cn';

type Filter = 'all' | 'unread' | 'action_required';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'action_required', label: 'Action required' },
];

export default function Notifications() {
  const [filter, setFilter] = useState<Filter>('all');
  const params = {
    unreadOnly: filter === 'unread',
    severity:
      filter === 'action_required' ? ('action_required' as NotificationSeverity) : undefined,
    limit: 50,
  };
  const { data, isLoading, isError } = useNotifications(params);
  const markAll = useMarkAllRead();
  const rows = data?.notifications ?? [];

  return (
    <div className="animate-rise-fade">
      <Masthead
        kicker="Inbox"
        title="Notifications"
        lede="Everything that needs your attention in one place. Dismiss what you've handled — the rest will wait."
        actions={
          <Button
            variant="ghost"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending || rows.every((r) => r.read_at != null)}
          >
            Mark all read
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-[0.8125rem] font-medium transition-colors',
              filter === f.id
                ? 'border-brand-500 bg-brand-50 text-brand-800'
                : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-rule bg-surface overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            <SkeletonLine />
            <SkeletonLine />
            <SkeletonLine />
          </div>
        ) : isError ? (
          <div className="p-6 text-[0.875rem] text-danger-bold">
            Couldn't load notifications. Try refreshing.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title="Nothing here."
              description={
                filter === 'all'
                  ? 'When the agents surface new work, it shows up here.'
                  : 'Try a different filter.'
              }
            />
          </div>
        ) : (
          <div className="divide-y divide-rule">
            {rows.map((row) => (
              <NotificationItem key={row.id} row={row} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
